import { PayloadWithMessage } from 'src/common/store/reducers/types';
import { moment } from 'src/common/types';
import { DateRange } from '../../../../../../store';

export interface OverviewState {
  parameters: PortfolioParameters;
  portfolioDetailResult?: PortfolioDetailResult;
  templates: InvestmentServiceTemplate[];
}

export interface PortfolioParameters {
  dates: DateRange;
  groupingType: GroupingType;
  showPositionsNotHeld: boolean;
  unitDecimalPlaces: number;
}

export enum GroupingType {
  AssetClass = 'Asset Class',
  Model = 'Model',
  Account = 'Account',
  Gics = 'Gics',
}

export interface PortfolioDetailResult {
  clientId: number;
  investmentServiceId: number;
  gainsLossesDetail: GainsLossesDetail;
  portfolioDetail: PortfolioDetail;
}

export interface GainsLossesDetail {
  costBase: number;
  gainLossPercentage: number;
  gainLossValue: number;
  totalCostBase: number;
  totalUnrealisedGainLoss: number;
  totalValue: number;
  unrealisedGainsLossesGroup: UnrealisedGainsLossesGroup[];
}

export interface ExpandedRow {
  [key: number]: number[];
}

export interface PortfolioDetail {
  clientId: number;
  marketValue: number;
  availableCash: number;
  totalEstimatedIncome: number;
  totalEstimatedYield: number;
  totalEstimatedGrossIncome: number;
  totalEstimatedGrossYield: number;
  totalReturnSinceInception: number;
  totalReturnSinceInceptionPercentage: number;
  items: AssetGroup[];
}

export interface AssetGroup {
  groupName: string;
  currentValue: number;
  currentWeightPercentage: number;
  targetValue: number;
  targetWeightPercentage: number;
  differenceValue: number;
  differenceWeightPercentage: number;
  items: AssetGroupItem[];
  isGroup: boolean;
  underweightAssetCount: number | null;
  overweightAssetCount: number | null;
  bandName: string | null;
  securityId: number | null;
  securityCode: string | null;
  securityName: string | null;
  marketCode: string | null;
  assetClassId: number | null;
  assetClass: string | null;
  unitPrice: number | null;
  priceDate: string | null;
  units: number | null;
  estimatedGrossIncome: number | null;
  estimatedYield: number | null;
  unsettledUnits: number | null;
  unsettledValue: number | null;
  estimatedIncome: number | null;
  estimatedGrossYield: number | null;
  returnSinceInception: number | null;
  returnSinceInceptionPercentage: number | null;
  nativeValue: number | null;
  nativePrice: number | null;
  currencyCode: string | null;
  forexRate: number | null;
}

export interface AssetGroupItem {
  securityName: string | null;
  securityCode: string | null;
  units: number | null;
  unitPrice: number | null;
  priceDate: moment | undefined;
  currentValue: number | null;
  currentWeightPercentage: number | null;
  currencyCode: string | null;
  targetValue: number | null;
  targetWeightPercentage: number | null;
  differenceValue: number;
  differenceWeightPercentage: number | null;
  unsettledValue: number | null;
  isGroup: boolean;
  groupName: string | null;
  underweightAssetCount: number | null;
  overweightAssetCount: number | null;
  bandName: string | null;
  securityId: number;
  marketCode: string | null;
  assetClassId: number;
  assetClass: string | null;
  unsettledUnits: number | null;
  estimatedIncome: number | null;
  estimatedYield: number | null;
  estimatedGrossIncome: number | null;
  estimatedGrossYield: number | null;
  nativeValue: number | null;
  nativePrice: number | null;
  forexRate: number | null;
  returnSinceInception: number | null;
  returnSinceInceptionPercentage: number | null;
  items: [];
}

export interface UnrealisedGainsLossesGroup extends MatchedSecurityTableFields {
  groupName: string;
  isGroup: boolean;
  items: UnrealisedGainsLossesDetail[];
  marketValue: number;
  subGroupName: string;
}

export interface MatchedSecurityTableFields {
  costBase: number | null;
  gainLossPercentage: number | null;
  gainLossValue: number;
}

export interface UnrealisedGainsLossesDetail extends MatchedSecurityTableFields {
  acquisitionDate: string | null;
  capitalLossPercentage: number | null;
  capitalLossValue: number | null;
  discountableGainPercentage: number | null;
  discountableGainValue: number | null;
  groupName: string;
  groupSubName: string;
  isGroup: boolean;
  items: UnrealisedGainsLossesDetail[];
  marketValue: number | null;
  otherGainPercentage: number | null;
  otherGainValue: number | null;
  tradeDate: string | null;
  unitPrice: number;
  unitCost: number;
  units: number;
}

export interface AssetCsvRowDetailed {
  'Asset Class': string;
  'Security Name': string;
  'Security Code': string | null;
  Units: string | null;
  'Cost Base': string | null;
  'Average Unit Cost': string | null;
  'Price (AUD)': string | null;
  'Price (NATIVE)': string | null;
  'Value (AUD)': string | null;
  'Value (NATIVE)': string | null;
  'Port. %': string | null;
  'Unrealised Gain/Loss': string | null;
  'Unrealised Gain/Loss %': string | null;
  'Return Since Inception': string | null;
  'Return Since Inception %': string | null;
  'Est. Income': string | null;
  'Est. Yield': string | null;
  'Est. Gross Income': string | null;
  'Est. Gross Yield': string | null;
  'Price Date': moment | undefined;
  'Native Currency': string | null;
  'Forex Rate': string | null;
}

export interface SecurityColumnVisibility {
  name: boolean;
  securityCode: boolean;
  units: boolean;
  costBase: boolean;
  unitCost: boolean;
  unitPrice: boolean;
  nativePrice: boolean;
  currentValue: boolean;
  nativeValue: boolean;
  currentWeightPercentage: boolean;
  gainLossPercentage: boolean;
  returnSinceInception: boolean;
  returnSinceInceptionPercentage: boolean;
  gainLossValue: boolean;
  estimatedIncome: boolean;
  estimatedYield: boolean;
  estimatedGrossIncome: boolean;
  estimatedGrossYield: boolean;
  priceDate: boolean;
  currencyCode: boolean;
  forexRate: boolean;
  targetValue?: boolean;
  targetWeight?: boolean;
  differenceValue?: boolean;
  differenceWeightPercentage?: boolean;
  unsettledUnits?: boolean;
  unsettledValue?: boolean;
}

export interface InvestmentServiceTemplate {
  investmentServiceTemplateVersionId: number;
  majorVersion: number;
  minorVersion: number;
  name: string;
  code: string;
}

export interface FetchPortfolioDetailPayload extends TriumphBaseRequest {
  clientId: number;
  investmentServiceId: number | null;
  parameters: PortfolioParameters;
  fromDate: string | null;
}

export interface FetchTemplatesPayload {
  afslId: number;
}

export interface CreateAndGetNewInvestmentServicePayload {
  clientId: number;
  serviceTypeId: number;
  investmentServiceTemplateVersionId: number | null;
}

export interface CreateAndGetNewInvestmentServiceResponse extends PayloadWithMessage {
  investmentServiceId?: number;
  investmentServiceVersionId?: number;
}

export interface TriumphBaseRequest {
  isDataFromTriumph?: boolean;
}

export interface ProcessCalculateHoldingsAndPerformanceRequest {
  clientIds: number[] | null;
  clientId: number | null;
  jobType: string;
}

export type PortfolioViewMode = 'compact' | 'detailed';
